import {
  COOKIES,
  FORM_IDS,
  KLAVIYO_REVISION_VERSION,
} from '@bulknutrients/bulk-shared-lib/constants';
import { isErrorResponse } from '@bulknutrients/bulk-shared-lib/interfaces/BulkAPI';
import type { Customer } from '@bulknutrients/bulk-shared-lib/interfaces/Customer';
import type { NewsItem as INewsItem } from '@bulknutrients/bulk-shared-lib/interfaces/Sanity';
import * as formsSchema from '@bulknutrients/bulk-shared-lib/lib/drizzle/forms/schema';
import { drizzle } from '@bulknutrients/bulk-shared-lib/lib/drizzle/instances';
import { checkCustomerSubmittedForm } from '@bulknutrients/bulk-shared-lib/lib/postgres/forms';
import { getFormById } from '@bulknutrients/bulk-shared-lib/lib/queries/sanity/forms';
import {
  cdnClient,
  client,
} from '@bulknutrients/bulk-shared-lib/lib/sanity/client';
import { getLast10 } from '@bulknutrients/bulk-shared-lib/lib/sanity/newsPages';
import { BulkErrorBoundary } from '@components/Alerts/ErrorBoundaryFallback';
import { Button } from '@components/Button';
import { ButtonType } from '@components/Button/Button';
import { HorizontalDivider } from '@components/HorizontalDivider/HorizontalDivider';
import { Icons } from '@components/Icons/Icons';
import { AccountLayout } from '@components/Layouts';
import { NewsItem } from '@components/News/NewsItem';
import { SkeletonImage, SkeletonText } from '@components/Placeholders/Skeleton';
import { PortableTextBlockRenderer } from '@components/PortableText/PortableText';
import { TooltipWrapper } from '@components/Tooltip/Tooltip';
import { Paragraph } from '@components/Typography/Paragraph/Paragraph';
import {
  RewardsLink,
  TextLink,
} from '@components/Typography/TextLink/TextLink';
import { useKlaviyo } from '@hooks/useKlaviyo';
import { customFetch } from '@lib/api/utils';
import { log } from '@lib/logs';
import type { PortableTextBlock } from '@portabletext/types';
import type { GetServerSideProps } from 'next';
import Link from 'next/link';
import { getCustomer } from 'pages/api/customer/current';
import { Pool } from 'pg';
import { type ReactElement, useEffect, useState } from 'react';

const ICON_SIZE = '3rem';

interface IResubscriptionAside {
  content: PortableTextBlock;
  text: string;
}

const getresubscribeAsideContent = async () => {
  const query = `//groq
  *[_type == "marketingCopy"] {
    "content": resubscribeContent,
    "text": resubscribeButtonText
  }`;

  const result = await client.fetch(query);

  if (!result) {
    const error = new Error('Unable to fetch details for resubscription aside');
    throw error;
  }

  return result[0] ?? null;
};

export const getSubscriptionStatus = async (
  email: string,
  cookies?: Partial<{ [key: string]: string }>
): Promise<{ sms: string; email: string } | null> => {
  try {
    const url = new URL('/api/profiles/', 'https://a.klaviyo.com');
    // filter for specific user
    // to note, you can only filter for profile or subscription fields, not both.
    url.searchParams.set('filter', `equals(email,'${email}')`);
    // include subscription information - returns email and sms
    url.searchParams.set('additional-fields[profile]', 'subscriptions');

    const options = {
      method: 'GET',
      headers: {
        accept: 'application/json',
        revision: KLAVIYO_REVISION_VERSION,
        Authorization:
          `Klaviyo-API-Key ${process.env.PRIVATE_KLAVIYO_API_KEY}` as string,
      },
    };

    const response = await customFetch(url.href, options);
    log({
      message: `Klaviyo GET /api/profiles response for ${email}: ${response.status}`,
      cookies,
      location: 'Account Home / getSubscriptionStatus',
    });

    if (response.ok) {
      const { data } = await response.json();
      // protect against newly created accounts getting redirected to here
      // klaviyo is still setting up profile I think and returns none.
      if (!data?.length) return null;
      /*
        consent reasons: UNSUBSCRIBED, SUBSCRIBED, NEVER_SUBSCRIBED
      */
      return {
        email: data[0].attributes.subscriptions.email.marketing.consent,
        sms: data[0].attributes.subscriptions.sms.marketing.consent,
      };
    }
    throw Error();
  } catch (e) {
    // only return something if successful, resubscription aside will only display if consent value is sent.
    log({
      error: e,
      cookies,
      location: 'Account Home / getSubscriptionStatus',
    });
    return null;
  }
};

function HowDidYouHear({
  name,
  form,
}: {
  name: string;
  form: null | PortableTextBlock;
}) {
  return (
    <>
      {form ? (
        <>
          <Paragraph>
            Thanks for joining the Bulk crew {name}! We're curious, how'd you
            originally find out about Bulk Nutrients?
          </Paragraph>
          <section className="mx-auto flex w-fit justify-center rounded-md bg-grey-light p-5 dark:bg-grey-darkest">
            <PortableTextBlockRenderer content={form} />
          </section>
        </>
      ) : (
        <Paragraph>Welcome back {name}!</Paragraph>
      )}
      <HorizontalDivider className="my-4 from-transparent via-grey-mid to-transparent" />
    </>
  );
}

function WheyCoolPoints({ points }: { points: number }) {
  return points > 500 ? (
    <Paragraph align="center">
      You've earned <strong>{points} Whey Cool Points</strong>! Find out how you
      can <RewardsLink>redeem your points</RewardsLink>.
    </Paragraph>
  ) : (
    <Paragraph align="center">
      You've earned <strong>{points} Whey Cool Points</strong>! Not quite enough
      to redeem yet - keep purchasing to hit the threshold.
    </Paragraph>
  );
}

export default function AccountHomePage({
  customer,
  newsItems,
  form,
  subscriptionStatus,
  resubscribeAsideContent,
}: {
  customer: Customer;
  newsItems: INewsItem[];
  form: null | PortableTextBlock;
  subscriptionStatus: { sms: string; email: string } | null;
  resubscribeAsideContent: IResubscriptionAside | null;
}): ReactElement {
  const { newsletterSubscribe } = useKlaviyo();
  const [disabled, setDisabled] = useState<boolean>(false);

  const [resubscriptionError, setResubscriptionError] = useState<
    string | undefined
  >();
  const [showResubscribeAside, setShowResubscribeAside] =
    useState<boolean>(false);
  const [storeCredit, setStoreCredit] = useState<number>();

  const homeNavigationItems = [
    {
      title: 'Reviews',
      description: "Review products you've purchased",
      icon: <Icons.Rewards.WriteReview size={ICON_SIZE} />,
      slug: '/account/reviews',
      disabled: !customer.loyaltyLion,
    },
    {
      title: 'Rewards',
      description: 'Earn and redeem whey cool points',
      icon: <Icons.Trophy.Outline size={ICON_SIZE} />,
      slug: '/account/rewards',
      disabled: !customer.loyaltyLion,
      tooltipText: 'You do not have a rewards account with us',
    },
    {
      title: 'Your Favourites',
      description: 'Favourite products for easy access',
      icon: <Icons.Heart.Outline size={ICON_SIZE} />,
      slug: '/account/favourites',
    },
    {
      title: 'Order History',
      description: 'View previous orders and reorder items',
      icon: <Icons.History size={ICON_SIZE} />,
      slug: '/account/orders',
    },
    {
      title: 'Saved Addresses',
      description: 'View or remove your saved addresses',
      icon: <Icons.AddressCard size={ICON_SIZE} />,
      slug: '/account/addresses',
    },
    {
      title: 'Login & Security',
      description: 'Edit login, name, and mobile number',
      icon: <Icons.Settings size={ICON_SIZE} />,
      slug: '/account/settings',
    },
    {
      title: 'Referral Program',
      description: 'Share your referral link and view earned rewards',
      icon: <Icons.CS.Chat size={ICON_SIZE} />,
      slug: '/account/referral-program',
    },
    {
      title: 'Communication Preferences',
      description: 'Edit advertising and communication preferences',
      icon: <Icons.Social.Email size={ICON_SIZE} />,
      slug: '/account/preferences',
    },
    {
      title: 'Customer Service & FAQs',
      description: 'Browse self-service options, FAQs, or contact us',
      icon: <Icons.CS.Phone size={ICON_SIZE} />,
      slug: '/contact-us',
    },
  ];

  useEffect(() => {
    let mounted = true;
    fetch('/api/customer/store-credit')
      .then((res) =>
        res.json().then((credit) => {
          if (!isErrorResponse(credit)) {
            if (mounted) setStoreCredit(credit.data);
          }
        })
      )
      .catch((err) => {
        // no store credit
        if (mounted) setStoreCredit(undefined);
      });

    return function cleanup() {
      mounted = false;
    };
  }, []);

  useEffect(() => {
    if (
      subscriptionStatus?.email &&
      subscriptionStatus.email !== 'SUBSCRIBED'
    ) {
      // check local storage if seen already
      // local storage is not accessible server side in getServerSideProps so check here
      // if unsubscribed and not already shown, display resubscribe aside
      if (['false', null].includes(localStorage.getItem('shownResubscribe')))
        setShowResubscribeAside(true);
    }
  }, [subscriptionStatus]);

  const closeResubscribeAside = () => {
    // only set in localStorage that customer has seen modal if an error has not occurred.
    localStorage.setItem('shownResubscribe', `${!resubscriptionError}`);
    setShowResubscribeAside(false);
  };

  const resubscribe = async () => {
    setDisabled(true);
    // reset to false so that aside appears every time customer unsubscribes.
    localStorage.setItem('shownResubscribe', 'false');
    const success = await newsletterSubscribe({
      email: customer.email,
      source: 'website_accountpage',
    });
    // if successful, just close modal. Else show error message.
    // if error occurs, aside will pop up on refresh again or next time account page is accessed.
    if (success) setShowResubscribeAside(false);
    else
      setResubscriptionError(
        'An unexpected error occurred. Please try again next time or contact our customer service.'
      );
  };

  return (
    <article>
      {showResubscribeAside && resubscribeAsideContent?.content && (
        <aside className="clear-both mx-0 my-7 grid border border-solid border-orange bg-orange-20 px-4! pb-0 pt-2.5 text-grey-dark dark:bg-orange-dark dark:text-white rounded-2xl">
          <button
            type="button"
            onClick={closeResubscribeAside}
            className="justify-self-end text-4xl text-black hover:cursor-pointer hover:text-orange dark:text-white dark:hover:text-orange"
          >
            <Icons.Close />
          </button>
          {!resubscriptionError ? (
            <>
              <PortableTextBlockRenderer
                content={resubscribeAsideContent.content}
              />
              <div className="mb-4 flex w-full justify-end">
                <Button
                  onClick={resubscribe}
                  disabled={disabled}
                  type={ButtonType.submit}
                  buttonStyle="primary"
                >
                  {resubscribeAsideContent.text}
                </Button>
              </div>
            </>
          ) : (
            <Paragraph align="center">{resubscriptionError}</Paragraph>
          )}
        </aside>
      )}
      <div className="text-center">
        <h1>Account Home</h1>
        <HowDidYouHear name={customer.firstName} form={form} />
        {storeCredit && storeCredit > 0 ? (
          <div className="my-4">
            Your account currently has{' '}
            <strong>${storeCredit?.toFixed(2)} store credit</strong> applied to
            it.
          </div>
        ) : (
          <></>
        )}
        <BulkErrorBoundary location="Account Home / Whey Cool Points">
          {customer.loyaltyLion?.points && customer.loyaltyLion.points > 0 ? (
            <WheyCoolPoints points={customer.loyaltyLion.points} />
          ) : null}
        </BulkErrorBoundary>
      </div>
      <BulkErrorBoundary location="Account Home / Navigation">
        <div className="mb-6 grid grid-cols-1 gap-4 sm:grid-cols-2">
          {homeNavigationItems.map((item) => (
            <div key={item.title} className="relative">
              <Link
                prefetch={process.env.NEXT_PUBLIC_PREFETCH === 'true'}
                href={item.slug}
                aria-label={item.title}
              >
                <div className="card grid min-h-[95px] grid-cols-[4rem_auto] items-center gap-2 bg-grey-light hover:border-orange! active:border-orange! dark:border-grey-light dark:bg-grey-darkest rounded-2xl">
                  <div className="flex flex-1 items-center justify-center">
                    {item.icon}
                  </div>
                  <div className="flex flex-col justify-center py-3 pr-2">
                    <h2 className="text-xl italic text-orange">{item.title}</h2>
                    <div className="line-clamp-2">{item.description}</div>
                  </div>
                </div>
              </Link>
              {item.disabled && (
                <TooltipWrapper text={item.tooltipText ?? ''}>
                  <div
                    key={item.title}
                    className="absolute bottom-0 h-full w-full bg-grey-mid/40 dark:bg-grey-dark/40"
                  />
                </TooltipWrapper>
              )}
            </div>
          ))}
        </div>
      </BulkErrorBoundary>
      <HorizontalDivider />
      <BulkErrorBoundary location="Account Home / News">
        <h2 className="italic">Latest Bulk Nutrients News</h2>
        {newsItems.map((item) => (
          <NewsItem
            key={item.title}
            item={item}
            baseUrl="https://www.bulknutrients.com.au/news"
          />
        ))}
        <Paragraph>
          View the complete <TextLink href="/news">News Archive</TextLink>
        </Paragraph>
      </BulkErrorBoundary>
    </article>
  );
}

export function AccountHomePageSkeleton(): ReactElement {
  return (
    <div className="flex flex-col h-screen w-narrow-page mx-auto">
      <div className="text-center">
        <h1>Account Home</h1>
        <SkeletonText heightPixels={100} />
      </div>
      <div className="mb-6 grid grid-cols-1 gap-4 sm:grid-cols-2">
        {[...Array(9)].map((_, index) => (
          <div key={index} className="flex gap-4 items-center">
            <div className="w-10 h-10">
              <SkeletonImage />
            </div>
            <SkeletonText heightPixels={80} />
          </div>
        ))}
      </div>
    </div>
  );
}

AccountHomePage.getLayout = function getLayout(page: ReactElement) {
  return <AccountLayout>{page}</AccountLayout>;
};

export const getServerSideProps: GetServerSideProps = async ({
  params,
  req,
}) => {
  // Get customer
  const { cookies } = req;
  const customerToken = cookies[COOKIES.customer];
  const impersonationToken = cookies[COOKIES.impersonation];
  if (!customerToken && !impersonationToken) {
    return {
      redirect: {
        destination: '/login',
        permanent: false,
      },
    };
  }

  const tokenToUse = customerToken || impersonationToken;

  const customer = await getCustomer(tokenToUse as string, cookies);
  // No customer, redirect
  if (!customer) {
    return {
      redirect: {
        destination: '/login',
        permanent: false,
      },
    };
  }

  const [newsItems, orders, subscriptionStatus, resubscribeAsideContent] =
    await Promise.all([
      getLast10().catch((error) => {
        log({
          error,
          location: 'Account Home / getServerSideProps get news items catch',
        });
        return [] as INewsItem[];
      }),
      (async () => {
        let orders = [] as any[];
        const ordersResponse = await customFetch(
          `${process.env.BIGCOMMERCE_STORE_API_URL}/v2/orders?customer_id=${customer.entityId}&limit=4&sort=date_created:desc`,
          {
            method: 'GET',
            headers: {
              Accept: 'application/json',
              'Content-Type': 'application/json',
              'X-Auth-Token': process.env.BIGCOMMERCE_STORE_API_TOKEN as string,
            },
          }
        );
        if (ordersResponse.ok && ordersResponse.status !== 204) {
          orders = (await ordersResponse.json()) ?? [];
        }
        return orders;
      })().catch((error) => {
        log({
          error,
          location: 'Account Home / getServerSideProps get orders catch',
          cookies,
        });
        return [] as any[];
      }),
      getSubscriptionStatus(customer.email.toLowerCase(), cookies).catch(
        (error) => {
          log({
            error,
            location:
              'Account Home / getServerSideProps get subscription status catch',
            cookies,
          });
          return null;
        }
      ),
      getresubscribeAsideContent().catch((error) => {
        log({
          error,
          location:
            'Account Home / getServerSideProps get resubscribe aside content catch',
          cookies,
        });
        return null;
      }),
    ]);

  const pool = new Pool({
    user: process.env.PGUSER_FORMS || '',
    password: process.env.PGPASSWORD_FORMS || '',
    database: process.env.PGDATABASE_FORMS || '',
    host: process.env.PGHOST_FORMS || '',
    port: 5432,
  });

  const db = drizzle(pool, { schema: formsSchema });

  // Check if customer already submitted referral source form
  let form: null | PortableTextBlock = null;
  if (orders.length > 0 && orders.length < 3) {
    try {
      const { isSubmitted, error } = await checkCustomerSubmittedForm({
        customerId: customer.entityId,
        formId: FORM_IDS.CUSTOMER_SOURCE,
        db,
        cookies,
      }).catch((err) => {
        log({
          error: err,
          location:
            'Account Home / getServerSideProps checkCustomerSubmittedForm catch',
          cookies,
        });
        return { isSubmitted: false, error: err };
      });

      if (error) {
        log({
          error,
          location:
            'Account Home / getServerSideProps checkCustomerSubmittedForm',
          cookies,
        });
      } else {
        if (!isSubmitted) {
          // Get form for new customers
          form = await cdnClient.fetch(getFormById, {
            id: FORM_IDS.CUSTOMER_SOURCE,
          });
        }
      }
    } catch (err) {
      log({
        error: err,
        location:
          'Account Home / getServerSideProps checkCustomerSubmittedForm try/catch',
        cookies,
      });
    }
  }

  // Return props
  return {
    props: {
      customer,
      newsItems,
      form,
      subscriptionStatus,
      resubscribeAsideContent,
    },
  };
};
